import { ChecklistDrawerContent } from '@/components/Checklists/ChecklistDrawerContent';
import { setChecklistDrawerInset } from '@/components/Checklists/checklistDrawerInset';
import {
  CHECKLIST_DRAWER_WIDTH as AI_ASSISTANT_DRAWER_WIDTH,
  CHECKLIST_DRAWER_Z_INDEX,
  NAV_HEIGHT,
} from '@/components/Checklists/checklistLayout';
import { Drawer, useBreakpoint } from '@palmetto/palmetto-components';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { DocumentExtractionsGroup } from './DocumentExtractionsGroup';

interface AiAssistantDrawerProps {
  accountId: string;
  queueId?: string;
  isOpen: boolean;
  onClose: () => void;
  onCompleteReview?: (targetStatus?: import('types').QueueStatus) => Promise<void>;
}

export const AiAssistantDrawer: React.FC<AiAssistantDrawerProps> = ({
  accountId,
  queueId,
  isOpen,
  onClose,
  onCompleteReview,
}) => {
  const { isPhone } = useBreakpoint();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const containerRef = useRef<any>(null);
  const [isFlagFlowActive, setIsFlagFlowActive] = useState(false);
  const handleFlagFlowChange = useCallback((active: boolean) => setIsFlagFlowActive(active), []);

  const coversLeftEdge = isOpen && !isPhone;
  useEffect(() => {
    setChecklistDrawerInset(coversLeftEdge ? AI_ASSISTANT_DRAWER_WIDTH : 0);
    return () => setChecklistDrawerInset(0);
  }, [coversLeftEdge]);

  useEffect(() => {
    const container: HTMLElement | null = containerRef.current;
    const stop = (event: Event) => event.stopPropagation();
    container?.addEventListener('wheel', stop, { passive: true });
    container?.addEventListener('touchmove', stop, { passive: true });
    return () => {
      container?.removeEventListener('wheel', stop);
      container?.removeEventListener('touchmove', stop);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      data-theme="core"
      className="**:overscroll-contain"
      style={{
        position: 'fixed',
        top: NAV_HEIGHT,
        left: 0,
        ...(isPhone ? { right: 0 } : { width: AI_ASSISTANT_DRAWER_WIDTH }),
        bottom: 0,
        zIndex: CHECKLIST_DRAWER_Z_INDEX,
        pointerEvents: isOpen ? 'auto' : 'none',
      }}
    >
      <Drawer
        isOpen={isOpen}
        onDismiss={onClose}
        hideOverlay={isPhone ? false : true}
        width={`${AI_ASSISTANT_DRAWER_WIDTH}px`}
        placement={isPhone ? 'bottom' : 'left'}
        title="AI Assistant"
        containerRef={containerRef}
      >
        <div className="flex min-h-full flex-col gap-16 bg-canvas-default p-8">
          {queueId && (
            <ChecklistDrawerContent
              queueId={queueId}
              onFlagFlowChange={handleFlagFlowChange}
              onCompleteReview={onCompleteReview}
            />
          )}
          {!isFlagFlowActive && <DocumentExtractionsGroup accountId={accountId} />}
        </div>
      </Drawer>
    </div>
  );
};
